import React, { useState } from 'react';
import {
  Box,
  Typography,
  Button,
  Stepper,
  Step,
  StepLabel,
  StepContent,
  Dialog,
  DialogTitle,
  DialogContent,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Alert,
  Divider,
  Chip
} from '@mui/material';
import {
  Help as HelpIcon,
  Close as CloseIcon,
  CheckCircle as CheckCircleIcon,
  Info as InfoIcon,
  Map as MapIcon,
  TableChart as TableIcon,
  Timeline as TimelineIcon,
  Settings as SettingsIcon,
  FilterList as FilterIcon,
  Lock as LockIcon,
  LockOpen as UnlockIcon,
  Person as PersonIcon,
  LocationOn as LocationIcon,
  Schedule as TimeIcon,
  Search as SearchIcon,
  ViewList as ViewListIcon,
  ZoomIn as ZoomIcon,
  PanTool as PanIcon
} from '@mui/icons-material';

interface ApplicationTutorialProps {
  open: boolean;
  onClose: () => void;
}

const ApplicationTutorial: React.FC<ApplicationTutorialProps> = ({
  open,
  onClose
}) => {
  const [activeStep, setActiveStep] = useState(0);

  const steps = [
    {
      label: 'Application Overview',
      content: (
        <Box>
          <Typography variant="h6" gutterBottom>
            Crime Scene Investigation Tool
          </Typography>
          <Typography paragraph>
            This application helps investigators analyze crime scenes by providing multiple views of the same data. 
            Each view offers a different perspective to help you understand the relationships between people, locations, evidence, and time.
          </Typography>
          
          <Box sx={{ mb: 2 }}>
            <Typography variant="subtitle1" gutterBottom>
              <strong>Main Views:</strong>
            </Typography>
            <List dense>
              <ListItem>
                <ListItemIcon><MapIcon color="primary" /></ListItemIcon>
                <ListItemText 
                  primary="Map View" 
                  secondary="Geographic visualization of crime scenes and evidence locations" 
                />
              </ListItem>
              <ListItem>
                <ListItemIcon><ViewListIcon color="primary" /></ListItemIcon>
                <ListItemText 
                  primary="Local View" 
                  secondary="Detailed floor plan view of specific locations" 
                />
              </ListItem>
                             <ListItem>
                 <ListItemIcon><TableIcon color="primary" /></ListItemIcon>
                 <ListItemText 
                   primary="Tabular View" 
                   secondary="Organized data tables for systematic analysis" 
                 />
               </ListItem>
              <ListItem>
                <ListItemIcon><TimelineIcon color="primary" /></ListItemIcon>
                <ListItemText 
                  primary="Timeline View" 
                  secondary="Chronological sequence of events and activities" 
                />
              </ListItem>
              <ListItem>
                <ListItemIcon><SettingsIcon color="primary" /></ListItemIcon>
                <ListItemText 
                  primary="Settings" 
                  secondary="Application configuration and preferences" 
                />
              </ListItem>
            </List>
          </Box>

          <Alert severity="info" sx={{ mt: 2 }}>
            <Typography variant="body2">
              <strong>Pro Tip:</strong> Use multiple views together to get a complete picture. 
              Start with Map View for geographic context, then use Timeline View to understand the sequence of events.
            </Typography>
          </Alert>
        </Box>
      )
    },
    {
      label: 'Map View',
      content: (
        <Box>
          <Typography variant="h6" gutterBottom>
            Map View - Geographic Analysis
          </Typography>
          <Typography paragraph>
            The Map View shows crime scenes, evidence locations, and movement patterns on a geographic map. 
            This helps you understand spatial relationships and geographic patterns in your investigation.
          </Typography>
          
          <Box sx={{ mb: 2 }}>
            <Typography variant="subtitle1" gutterBottom>
              <strong>Key Features:</strong>
            </Typography>
            <List dense>
              <ListItem>
                <ListItemIcon><PersonIcon /></ListItemIcon>
                <ListItemText 
                  primary="People Markers" 
                  secondary="Green markers show where people were located at specific times" 
                />
              </ListItem>
              <ListItem>
                <ListItemIcon><LocationIcon /></ListItemIcon>
                <ListItemText 
                  primary="Location Markers" 
                  secondary="Blue markers indicate crime scenes and important locations" 
                />
              </ListItem>
              <ListItem>
                <ListItemIcon><InfoIcon /></ListItemIcon>
                <ListItemText 
                  primary="Evidence Markers" 
                  secondary="Orange markers show where evidence was discovered" 
                />
              </ListItem>
              <ListItem>
                <ListItemIcon><PanIcon /></ListItemIcon>
                <ListItemText 
                  primary="Movement Lines" 
                  secondary="Lines show how people moved between locations over time" 
                />
              </ListItem>
            </List>
          </Box>

          <Box sx={{ mb: 2 }}>
            <Typography variant="subtitle1" gutterBottom>
              <strong>Navigation:</strong>
            </Typography>
            <List dense>
              <ListItem>
                <ListItemIcon><ZoomIcon /></ListItemIcon>
                <ListItemText 
                  primary="Zoom Controls" 
                  secondary="Use mouse wheel or zoom buttons to focus on specific areas" 
                />
              </ListItem>
              <ListItem>
                <ListItemIcon><PanIcon /></ListItemIcon>
                <ListItemText 
                  primary="Pan" 
                  secondary="Click and drag to move around the map" 
                />
              </ListItem>
              <ListItem>
                <ListItemIcon><CheckCircleIcon /></ListItemIcon>
                <ListItemText 
                  primary="Click Markers" 
                  secondary="Click any marker to see detailed information" 
                />
              </ListItem>
            </List>
          </Box>

          <Alert severity="success" sx={{ mt: 2 }}>
            <Typography variant="body2">
              <strong>Investigation Use:</strong> Use Map View to identify geographic patterns, 
              track suspect movements, and understand the spatial relationship between different crime scenes.
            </Typography>
          </Alert>
        </Box>
      )
    },
    {
      label: 'Local View',
      content: (
        <Box>
          <Typography variant="h6" gutterBottom>
            Local View - Floor Plan Analysis
          </Typography>
          <Typography paragraph>
            The Local View provides detailed floor plan visualization of specific locations. 
            This helps you understand the internal layout and positioning within crime scenes.
          </Typography>
          
          <Box sx={{ mb: 2 }}>
            <Typography variant="subtitle1" gutterBottom>
              <strong>Features:</strong>
            </Typography>
            <List dense>
              <ListItem>
                <ListItemIcon><PersonIcon /></ListItemIcon>
                <ListItemText 
                  primary="People Positions" 
                  secondary="See exactly where people were positioned within rooms" 
                />
              </ListItem>
              <ListItem>
                <ListItemIcon><InfoIcon /></ListItemIcon>
                <ListItemText 
                  primary="Evidence Placement" 
                  secondary="Detailed placement of evidence items within the scene" 
                />
              </ListItem>
              <ListItem>
                <ListItemIcon><PanIcon /></ListItemIcon>
                <ListItemText 
                  primary="Movement Paths" 
                  secondary="Track how people moved within the location" 
                />
              </ListItem>
              <ListItem>
                <ListItemIcon><ZoomIcon /></ListItemIcon>
                <ListItemText 
                  primary="Room Details" 
                  secondary="Zoom in to see fine details of specific areas" 
                />
              </ListItem>
            </List>
          </Box>

          <Box sx={{ mb: 2 }}>
            <Typography variant="subtitle1" gutterBottom>
              <strong>Investigation Workflow:</strong>
            </Typography>
            <List dense>
              <ListItem>
                <ListItemIcon><CheckCircleIcon /></ListItemIcon>
                <ListItemText 
                  primary="1. Select Location" 
                  secondary="Choose the specific location you want to analyze" 
                />
              </ListItem>
              <ListItem>
                <ListItemIcon><CheckCircleIcon /></ListItemIcon>
                <ListItemText 
                  primary="2. Review Layout" 
                  secondary="Understand the physical layout and room arrangement" 
                />
              </ListItem>
              <ListItem>
                <ListItemIcon><CheckCircleIcon /></ListItemIcon>
                <ListItemText 
                  primary="3. Analyze Positions" 
                  secondary="See where people were positioned relative to evidence" 
                />
              </ListItem>
            </List>
          </Box>

          <Alert severity="warning" sx={{ mt: 2 }}>
            <Typography variant="body2">
              <strong>Note:</strong> Local View is most useful when you have detailed floor plan data. 
              Use this view to reconstruct the scene and understand spatial relationships within buildings.
            </Typography>
          </Alert>
        </Box>
      )
    },
    {
             label: 'Tabular View',
       content: (
         <Box>
           <Typography variant="h6" gutterBottom>
             Tabular View - Data Analysis
           </Typography>
                     <Typography paragraph>
             The Tabular View presents all investigation data in organized, sortable tables. 
             This view is perfect for systematic analysis and finding specific information quickly.
           </Typography>
          
          <Box sx={{ mb: 2 }}>
            <Typography variant="subtitle1" gutterBottom>
              <strong>Table Types:</strong>
            </Typography>
            <List dense>
              <ListItem>
                <ListItemIcon><PersonIcon /></ListItemIcon>
                <ListItemText 
                  primary="People Table" 
                  secondary="All individuals involved in the investigation" 
                />
              </ListItem>
              <ListItem>
                <ListItemIcon><LocationIcon /></ListItemIcon>
                <ListItemText 
                  primary="Locations Table" 
                  secondary="All crime scenes and important locations" 
                />
              </ListItem>
              <ListItem>
                <ListItemIcon><InfoIcon /></ListItemIcon>
                <ListItemText 
                  primary="Evidence Table" 
                  secondary="All evidence items with details and locations" 
                />
              </ListItem>
              <ListItem>
                <ListItemIcon><TimeIcon /></ListItemIcon>
                <ListItemText 
                  primary="Events Table" 
                  secondary="Chronological list of all events and activities" 
                />
              </ListItem>
            </List>
          </Box>

          <Box sx={{ mb: 2 }}>
            <Typography variant="subtitle1" gutterBottom>
              <strong>Table Features:</strong>
            </Typography>
            <List dense>
              <ListItem>
                <ListItemIcon><SearchIcon /></ListItemIcon>
                <ListItemText 
                  primary="Search & Filter" 
                  secondary="Quickly find specific information using search and filters" 
                />
              </ListItem>
              <ListItem>
                <ListItemIcon><FilterIcon /></ListItemIcon>
                <ListItemText 
                  primary="Sort Columns" 
                  secondary="Click column headers to sort data by any field" 
                />
              </ListItem>
              <ListItem>
                <ListItemIcon><CheckCircleIcon /></ListItemIcon>
                <ListItemText 
                  primary="Click Rows" 
                  secondary="Click any row to see detailed information" 
                />
              </ListItem>
              <ListItem>
                <ListItemIcon><ViewListIcon /></ListItemIcon>
                <ListItemText 
                  primary="Export Data" 
                  secondary="Export table data for reports or further analysis" 
                />
              </ListItem>
            </List>
          </Box>

                     <Alert severity="info" sx={{ mt: 2 }}>
             <Typography variant="body2">
               <strong>Pro Tip:</strong> Use Tabular View when you need to find specific information quickly or 
               when you want to systematically review all data in a structured format.
             </Typography>
           </Alert>
        </Box>
      )
    },
    {
      label: 'Timeline View',
      content: (
        <Box>
          <Typography variant="h6" gutterBottom>
            Timeline View - Chronological Analysis
          </Typography>
          <Typography paragraph>
            The Timeline View shows all events, movements, and activities in chronological order. 
            This helps you understand the sequence of events and identify temporal patterns.
          </Typography>
          
          <Box sx={{ mb: 2 }}>
            <Typography variant="subtitle1" gutterBottom>
              <strong>Timeline Features:</strong>
            </Typography>
            <List dense>
              <ListItem>
                <ListItemIcon><TimeIcon /></ListItemIcon>
                <ListItemText 
                  primary="Chronological Order" 
                  secondary="All events displayed in exact time sequence" 
                />
              </ListItem>
              <ListItem>
                <ListItemIcon><PersonIcon /></ListItemIcon>
                <ListItemText 
                  primary="People Activities" 
                  secondary="Track what each person was doing at specific times" 
                />
              </ListItem>
              <ListItem>
                <ListItemIcon><LocationIcon /></ListItemIcon>
                <ListItemText 
                  primary="Location Changes" 
                  secondary="See when people moved between locations" 
                />
              </ListItem>
              <ListItem>
                <ListItemIcon><InfoIcon /></ListItemIcon>
                <ListItemText 
                  primary="Evidence Discovery" 
                  secondary="Track when evidence was found and by whom" 
                />
              </ListItem>
            </List>
          </Box>

          <Box sx={{ mb: 2 }}>
            <Typography variant="subtitle1" gutterBottom>
              <strong>Timeline Controls:</strong>
            </Typography>
            <List dense>
              <ListItem>
                <ListItemIcon><ZoomIcon /></ListItemIcon>
                <ListItemText 
                  primary="Time Range Slider" 
                  secondary="Focus on specific time periods by adjusting the range" 
                />
              </ListItem>
              <ListItem>
                <ListItemIcon><FilterIcon /></ListItemIcon>
                <ListItemText 
                  primary="Filter by Type" 
                  secondary="Show only specific types of events (movements, evidence, etc.)" 
                />
              </ListItem>
              <ListItem>
                <ListItemIcon><PersonIcon /></ListItemIcon>
                <ListItemText 
                  primary="Filter by Person" 
                  secondary="Focus on activities of specific individuals" 
                />
              </ListItem>
              <ListItem>
                <ListItemIcon><CheckCircleIcon /></ListItemIcon>
                <ListItemText 
                  primary="Click Events" 
                  secondary="Click any event to see detailed information" 
                />
              </ListItem>
            </List>
          </Box>

          <Alert severity="success" sx={{ mt: 2 }}>
            <Typography variant="body2">
              <strong>Investigation Use:</strong> Use Timeline View to establish alibis, 
              identify gaps in time, and understand the sequence of events leading up to and following the crime.
            </Typography>
          </Alert>
        </Box>
      )
    },
    {
      label: 'Filters & Locks',
      content: (
        <Box>
          <Typography variant="h6" gutterBottom>
            Filters and Attribute Locks
          </Typography>
          <Typography paragraph>
            Filters and attribute locks help you focus your analysis on specific aspects of the investigation. 
            These tools are available across all views to help you drill down into relevant data.
          </Typography>
          
          <Box sx={{ mb: 2 }}>
            <Typography variant="subtitle1" gutterBottom>
              <strong>Attribute Filters:</strong>
            </Typography>
            <List dense>
              <ListItem>
                <ListItemIcon><PersonIcon /></ListItemIcon>
                <ListItemText 
                  primary="People Filter" 
                  secondary="Show only data related to specific individuals" 
                />
              </ListItem>
              <ListItem>
                <ListItemIcon><LocationIcon /></ListItemIcon>
                <ListItemText 
                  primary="Location Filter" 
                  secondary="Focus on specific locations or crime scenes" 
                />
              </ListItem>
              <ListItem>
                <ListItemIcon><TimeIcon /></ListItemIcon>
                <ListItemText 
                  primary="Time Filter" 
                  secondary="Limit data to specific time periods" 
                />
              </ListItem>
              <ListItem>
                <ListItemIcon><InfoIcon /></ListItemIcon>
                <ListItemText 
                  primary="Evidence Filter" 
                  secondary="Show only specific types of evidence" 
                />
              </ListItem>
            </List>
          </Box>

          <Box sx={{ mb: 2 }}>
            <Typography variant="subtitle1" gutterBottom>
              <strong>Attribute Locks:</strong>
            </Typography>
            <List dense>
              <ListItem>
                <ListItemIcon><LockIcon /></ListItemIcon>
                <ListItemText 
                  primary="Lock People" 
                  secondary="Keep focus on specific individuals across all views" 
                />
              </ListItem>
              <ListItem>
                <ListItemIcon><LockIcon /></ListItemIcon>
                <ListItemText 
                  primary="Lock Location" 
                  secondary="Maintain focus on specific locations" 
                />
              </ListItem>
              <ListItem>
                <ListItemIcon><LockIcon /></ListItemIcon>
                <ListItemText 
                  primary="Lock Time" 
                  secondary="Keep time range consistent across views" 
                />
              </ListItem>
              <ListItem>
                <ListItemIcon><UnlockIcon /></ListItemIcon>
                <ListItemText 
                  primary="Clear Locks" 
                  secondary="Remove all locks to see full dataset" 
                />
              </ListItem>
            </List>
          </Box>

          <Box sx={{ mb: 2 }}>
            <Typography variant="subtitle1" gutterBottom>
              <strong>Workflow Tips:</strong>
            </Typography>
            <List dense>
              <ListItem>
                <ListItemIcon><CheckCircleIcon /></ListItemIcon>
                <ListItemText 
                  primary="Start Broad" 
                  secondary="Begin with full dataset to get overview" 
                />
              </ListItem>
              <ListItem>
                <ListItemIcon><CheckCircleIcon /></ListItemIcon>
                <ListItemText 
                  primary="Apply Filters" 
                  secondary="Use filters to focus on relevant data" 
                />
              </ListItem>
              <ListItem>
                <ListItemIcon><CheckCircleIcon /></ListItemIcon>
                <ListItemText 
                  primary="Use Locks" 
                  secondary="Lock important attributes to maintain focus" 
                />
              </ListItem>
              <ListItem>
                <ListItemIcon><CheckCircleIcon /></ListItemIcon>
                <ListItemText 
                  primary="Switch Views" 
                  secondary="Use different views with same filters for different perspectives" 
                />
              </ListItem>
            </List>
          </Box>

          <Alert severity="warning" sx={{ mt: 2 }}>
            <Typography variant="body2">
              <strong>Remember:</strong> Filters and locks work together. Use filters to narrow down data, 
              then use locks to maintain that focus as you switch between different views.
            </Typography>
          </Alert>
        </Box>
      )
    },
    {
      label: 'Investigation Workflows',
      content: (
        <Box>
          <Typography variant="h6" gutterBottom>
            Common Investigation Workflows
          </Typography>
          <Typography paragraph>
            Here are some common workflows that combine multiple views and features for effective investigation analysis.
          </Typography>
          
          <Box sx={{ mb: 3 }}>
            <Typography variant="subtitle1" gutterBottom>
              <strong>Workflow 1: Suspect Movement Analysis</strong>
            </Typography>
            <Typography paragraph>
              <strong>Steps:</strong>
            </Typography>
            <List dense>
              <ListItem>
                <ListItemIcon><CheckCircleIcon /></ListItemIcon>
                <ListItemText 
                  primary="1. Start with Map View" 
                  secondary="Get geographic overview of all locations" 
                />
              </ListItem>
              <ListItem>
                <ListItemIcon><CheckCircleIcon /></ListItemIcon>
                <ListItemText 
                  primary="2. Filter by suspect" 
                  secondary="Use people filter to focus on specific suspect" 
                />
              </ListItem>
              <ListItem>
                <ListItemIcon><CheckCircleIcon /></ListItemIcon>
                <ListItemText 
                  primary="3. Switch to Timeline View" 
                  secondary="See chronological sequence of suspect's movements" 
                />
              </ListItem>
              <ListItem>
                <ListItemIcon><CheckCircleIcon /></ListItemIcon>
                <ListItemText 
                  primary="4. Use Local View" 
                  secondary="Examine specific locations where suspect was present" 
                />
              </ListItem>
            </List>
          </Box>

          <Box sx={{ mb: 3 }}>
            <Typography variant="subtitle1" gutterBottom>
              <strong>Workflow 2: Evidence Analysis</strong>
            </Typography>
            <Typography paragraph>
              <strong>Steps:</strong>
            </Typography>
            <List dense>
                             <ListItem>
                 <ListItemIcon><CheckCircleIcon /></ListItemIcon>
                 <ListItemText 
                   primary="1. Use Tabular View" 
                   secondary="Review all evidence in organized table" 
                 />
               </ListItem>
              <ListItem>
                <ListItemIcon><CheckCircleIcon /></ListItemIcon>
                <ListItemText 
                  primary="2. Filter by evidence type" 
                  secondary="Focus on specific types of evidence" 
                />
              </ListItem>
              <ListItem>
                <ListItemIcon><CheckCircleIcon /></ListItemIcon>
                <ListItemText 
                  primary="3. Switch to Map View" 
                  secondary="See geographic distribution of evidence" 
                />
              </ListItem>
              <ListItem>
                <ListItemIcon><CheckCircleIcon /></ListItemIcon>
                <ListItemText 
                  primary="4. Use Timeline View" 
                  secondary="Understand when evidence was discovered" 
                />
              </ListItem>
            </List>
          </Box>

          <Box sx={{ mb: 3 }}>
            <Typography variant="subtitle1" gutterBottom>
              <strong>Workflow 3: Witness Corroboration</strong>
            </Typography>
            <Typography paragraph>
              <strong>Steps:</strong>
            </Typography>
            <List dense>
              <ListItem>
                <ListItemIcon><CheckCircleIcon /></ListItemIcon>
                <ListItemText 
                  primary="1. Start with Timeline View" 
                  secondary="See all events in chronological order" 
                />
              </ListItem>
              <ListItem>
                <ListItemIcon><CheckCircleIcon /></ListItemIcon>
                <ListItemText 
                  primary="2. Filter by witness" 
                  secondary="Focus on specific witness statements" 
                />
              </ListItem>
              <ListItem>
                <ListItemIcon><CheckCircleIcon /></ListItemIcon>
                <ListItemText 
                  primary="3. Use Map View" 
                  secondary="Verify witness locations and movements" 
                />
              </ListItem>
                             <ListItem>
                 <ListItemIcon><CheckCircleIcon /></ListItemIcon>
                 <ListItemText 
                   primary="4. Compare with Tabular View" 
                   secondary="Cross-reference witness statements with other data" 
                 />
               </ListItem>
            </List>
          </Box>

          <Alert severity="success" sx={{ mt: 2 }}>
            <Typography variant="body2">
              <strong>Pro Tip:</strong> Always start with a broad view to get context, then use filters to focus on specific aspects. 
              Switch between views to get different perspectives on the same data.
            </Typography>
          </Alert>
        </Box>
      )
    },
    {
      label: 'Settings & Configuration',
      content: (
        <Box>
          <Typography variant="h6" gutterBottom>
            Settings and Configuration
          </Typography>
          <Typography paragraph>
            The Settings view allows you to configure the application to match your investigation needs and preferences.
          </Typography>
          
          <Box sx={{ mb: 2 }}>
            <Typography variant="subtitle1" gutterBottom>
              <strong>Display Settings:</strong>
            </Typography>
            <List dense>
              <ListItem>
                <ListItemIcon><CheckCircleIcon /></ListItemIcon>
                <ListItemText 
                  primary="Theme Selection" 
                  secondary="Choose light or dark theme for better visibility" 
                />
              </ListItem>
              <ListItem>
                <ListItemIcon><CheckCircleIcon /></ListItemIcon>
                <ListItemText 
                  primary="Map Style" 
                  secondary="Select different map styles for better contrast" 
                />
              </ListItem>
              <ListItem>
                <ListItemIcon><CheckCircleIcon /></ListItemIcon>
                <ListItemText 
                  primary="Default View" 
                  secondary="Set which view opens when you start the application" 
                />
              </ListItem>
              <ListItem>
                <ListItemIcon><CheckCircleIcon /></ListItemIcon>
                <ListItemText 
                  primary="Time Format" 
                  secondary="Choose 12-hour or 24-hour time format" 
                />
              </ListItem>
            </List>
          </Box>

          <Box sx={{ mb: 2 }}>
            <Typography variant="subtitle1" gutterBottom>
              <strong>Data Settings:</strong>
            </Typography>
            <List dense>
              <ListItem>
                <ListItemIcon><CheckCircleIcon /></ListItemIcon>
                <ListItemText 
                  primary="Auto-refresh" 
                  secondary="Automatically update data when new information is available" 
                />
              </ListItem>
              <ListItem>
                <ListItemIcon><CheckCircleIcon /></ListItemIcon>
                <ListItemText 
                  primary="Data Retention" 
                  secondary="Set how long to keep historical data" 
                />
              </ListItem>
              <ListItem>
                <ListItemIcon><CheckCircleIcon /></ListItemIcon>
                <ListItemText 
                  primary="Export Format" 
                  secondary="Choose preferred format for data exports" 
                />
              </ListItem>
            </List>
          </Box>

          <Box sx={{ mb: 2 }}>
            <Typography variant="subtitle1" gutterBottom>
              <strong>Performance Settings:</strong>
            </Typography>
            <List dense>
              <ListItem>
                <ListItemIcon><CheckCircleIcon /></ListItemIcon>
                <ListItemText 
                  primary="Map Detail Level" 
                  secondary="Adjust map detail for better performance on slower devices" 
                />
              </ListItem>
              <ListItem>
                <ListItemIcon><CheckCircleIcon /></ListItemIcon>
                <ListItemText 
                  primary="Animation Speed" 
                  secondary="Control animation speed in timeline and map views" 
                />
              </ListItem>
              <ListItem>
                <ListItemIcon><CheckCircleIcon /></ListItemIcon>
                <ListItemText 
                  primary="Cache Settings" 
                  secondary="Configure data caching for faster loading" 
                />
              </ListItem>
            </List>
          </Box>

          <Alert severity="info" sx={{ mt: 2 }}>
            <Typography variant="body2">
              <strong>Tip:</strong> Take time to configure settings that match your investigation workflow. 
              Proper settings can significantly improve your efficiency and the quality of your analysis.
            </Typography>
          </Alert>
        </Box>
      )
    }
  ];

  const handleNext = () => {
    setActiveStep((prevActiveStep) => prevActiveStep + 1);
  };

  const handleBack = () => {
    setActiveStep((prevActiveStep) => prevActiveStep - 1);
  };

  const handleReset = () => {
    setActiveStep(0);
  };

  return (
    <Dialog 
      open={open} 
      onClose={onClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: { height: '80vh' }
      }}
    >
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <HelpIcon color="primary" />
          <Typography variant="h6">Application Tutorial</Typography>
        </Box>
        <Button onClick={onClose} startIcon={<CloseIcon />}>
          Close
        </Button>
      </DialogTitle>
      
      <DialogContent sx={{ p: 0 }}>
        <Box sx={{ display: 'flex', height: '100%' }}>
          {/* Stepper */}
          <Box sx={{ width: 300, borderRight: 1, borderColor: 'divider', p: 2 }}>
            <Stepper activeStep={activeStep} orientation="vertical">
              {steps.map((step, index) => (
                <Step key={step.label}>
                  <StepLabel>
                    <Typography variant="subtitle2">{step.label}</Typography>
                  </StepLabel>
                  <StepContent>
                    <Box sx={{ mb: 2 }}>
                      <div>
                        <Button
                          variant="contained"
                          onClick={handleNext}
                          sx={{ mt: 1, mr: 1 }}
                          size="small"
                        >
                          {index === steps.length - 1 ? 'Finish' : 'Continue'}
                        </Button>
                        <Button
                          disabled={index === 0}
                          onClick={handleBack}
                          sx={{ mt: 1, mr: 1 }}
                          size="small"
                        >
                          Back
                        </Button>
                      </div>
                    </Box>
                  </StepContent>
                </Step>
              ))}
            </Stepper>
            {activeStep === steps.length && (
              <Box sx={{ p: 2 }}>
                <Typography variant="h6" gutterBottom>
                  Tutorial Complete!
                </Typography>
                <Typography paragraph>
                  You now know how to effectively use all the features of the crime scene investigation tool.
                </Typography>
                <Button onClick={handleReset} variant="outlined">
                  Restart Tutorial
                </Button>
              </Box>
            )}
          </Box>

          {/* Content */}
          <Box sx={{ flex: 1, p: 3, overflowY: 'auto' }}>
            {activeStep < steps.length && (
              <Box>
                {steps[activeStep].content}
                
                <Box sx={{ mt: 3, display: 'flex', justifyContent: 'space-between' }}>
                  <Button
                    disabled={activeStep === 0}
                    onClick={handleBack}
                  >
                    Back
                  </Button>
                  <Button
                    variant="contained"
                    onClick={handleNext}
                  >
                    {activeStep === steps.length - 1 ? 'Finish' : 'Next'}
                  </Button>
                </Box>
              </Box>
            )}
          </Box>
        </Box>
      </DialogContent>
    </Dialog>
  );
};

export default ApplicationTutorial;
